import { groupBy, sum } from "@common/helpers/collections";
import type { DensityChartDay, DensityChartSeries } from "./DensityChart.lib";
import { PLOT_HEIGHT } from "./DensityChart.lib";
import type {
    DensityChartBucket,
    DensityChartBucketUnit,
} from "./densityBuckets.lib";
import { bucketSeriesTotal, toBarIndex } from "./densityBuckets.lib";

/** One series' part of a bar: `bucket` is the bucket's start, `y1` and `y2` its ends in px above the baseline. */
export interface DensityBarSegment {
    bucket: string;
    seriesId: string;
    y1: number;
    y2: number;
}

const CLIP_RATIO = 3;
const MIN_BUCKETS_TO_CLIP = 4;

/**
 * The height above which a bar is clipped: a few times the 90th percentile
 * of busy buckets, so one 20k import doesn't flatten the rest. Null when no
 * bucket stands out or there are too few to tell.
 */
export function getClipThreshold(totals: number[]): number | null {
    const busyTotals = totals
        .filter((total) => total > 0)
        .sort((first, second) => first - second);
    if (busyTotals.length < MIN_BUCKETS_TO_CLIP) {
        return null;
    }
    const percentile =
        busyTotals[Math.floor(0.9 * (busyTotals.length - 1))] ?? 0;
    const threshold = Math.max(percentile * CLIP_RATIO, 1);
    const largest = busyTotals[busyTotals.length - 1] ?? 0;
    return largest > threshold ? threshold : null;
}

interface BuildBarsParams {
    buckets: DensityChartBucket[];
    unit: DensityChartBucketUnit;
    days: DensityChartDay[];
    series: DensityChartSeries[];
}

/** The space between two segments of a bar. */
export const SEGMENT_GAP_PX = 2;

/**
 * The shortest a segment with any count draws, so one change among thousands
 * stays visible. Under a busier bar shorter than that needs, the minimum and
 * the gaps shrink together.
 */
export const MIN_SEGMENT_HEIGHT_PX = 4;

const minBarPx = (segmentCount: number) =>
    segmentCount * MIN_SEGMENT_HEIGHT_PX +
    Math.max(segmentCount - 1, 0) * SEGMENT_GAP_PX;

/**
 * Splits `contentPx` between the values in proportion, lifting short ones to
 * `minPx` and taking the lift from the taller ones. A tall one the lift
 * pushes under `minPx` turns short too, until none does.
 */
function toSegmentHeights({
    values,
    contentPx,
    minPx,
}: {
    values: number[];
    contentPx: number;
    minPx: number;
}): number[] {
    const total = sum(values);
    const shares = values.map((value) => (value / total) * contentPx);
    const shortIndexes = new Set<number>();
    for (;;) {
        const tallTotal = sum(
            shares.filter((_, index) => !shortIndexes.has(index)),
        );
        const tallScale =
            tallTotal > 0
                ? Math.max(contentPx - shortIndexes.size * minPx, 0) / tallTotal
                : 0;
        const heights = shares.map((share, index) =>
            shortIndexes.has(index) ? minPx : share * tallScale,
        );
        const newShortIndexes = heights.flatMap((height, index) =>
            !shortIndexes.has(index) && height < minPx ? [index] : [],
        );
        if (newShortIndexes.length === 0) {
            return heights;
        }
        newShortIndexes.forEach((index) => shortIndexes.add(index));
    }
}

/**
 * Each bar's height: its share of the tallest, or the height its segments'
 * minimums and gaps need, capped at the height of every busier bar so a bar
 * never stands taller than one with a larger total.
 */
function toBarHeights(
    bars: ReadonlyArray<{ total: number; heightPx: number }>,
): number[] {
    const capByTotal = new Map<number, number>();
    let cap = Infinity;
    for (const { total, heightPx } of [...bars].sort(
        (first, second) => second.total - first.total,
    )) {
        cap = Math.min(cap, heightPx);
        capByTotal.set(total, cap);
    }
    return bars.map(({ total, heightPx }) => capByTotal.get(total) ?? heightPx);
}

/**
 * The segments of each bar with any count, bottom first, in px above the
 * baseline. Bar heights scale to the tallest bar `days` add up to, drawn or
 * not, whose segments and gaps fill `PLOT_HEIGHT`, and clip against all of
 * them too; clipped bars keep their mix at the clip height. The tooltip
 * still shows the real count.
 */
export function buildBars({
    buckets,
    unit,
    days,
    series,
}: BuildBarsParams): DensityBarSegment[] {
    const dayByDate = new Map(days.map((day) => [day.date, day]));
    const columns = buckets.map((bucket) => {
        const values = series
            .map(({ id }) => ({
                seriesId: id,
                value: bucketSeriesTotal({ bucket, dayByDate, seriesId: id }),
            }))
            .filter(({ value }) => value > 0);
        const total = sum(values.map(({ value }) => value));
        return { bucket: bucket.start, values, total };
    });
    const loadedTotals = Object.values(
        groupBy(days, ({ date }) => toBarIndex({ date, unit })),
    ).map((barDays) =>
        sum(
            barDays.flatMap(({ values }) =>
                series.map(({ id }) => values[id] ?? 0),
            ),
        ),
    );
    const threshold = getClipThreshold(loadedTotals);
    const toClippedTotal = (total: number) =>
        threshold === null ? total : Math.min(total, threshold);
    const tallest = Math.max(...loadedTotals.map(toClippedTotal), 1);
    const barHeights = toBarHeights(
        columns.map(({ values, total }) => ({
            total: toClippedTotal(total),
            heightPx: Math.max(
                (toClippedTotal(total) / tallest) * PLOT_HEIGHT,
                minBarPx(values.length),
            ),
        })),
    );
    return columns.flatMap(({ bucket, values }, columnIndex) => {
        const barPx = barHeights[columnIndex] ?? 0;
        const scale = Math.min(barPx / minBarPx(values.length), 1);
        const gapPx = SEGMENT_GAP_PX * scale;
        const heights = toSegmentHeights({
            values: values.map(({ value }) => value),
            contentPx: barPx - gapPx * Math.max(values.length - 1, 0),
            minPx: MIN_SEGMENT_HEIGHT_PX * scale,
        });
        let y1 = 0;
        return values.map(({ seriesId }, index) => {
            const segment = {
                bucket,
                seriesId,
                y1,
                y2: y1 + (heights[index] ?? 0),
            };
            y1 = segment.y2 + gapPx;
            return segment;
        });
    });
}

interface SegmentAtPointParams {
    /** One bar's segments. */
    segments: ReadonlyArray<Pick<DensityBarSegment, "seriesId" | "y1" | "y2">>;
    plotHeight: number;
    /** Pointer distance from the top of the track, in px. */
    y: number;
}

/**
 * Each segment also takes half the gap on either side, so moving across a
 * bar never falls between segments; a point two of them share goes to the
 * nearer centre.
 */
export function segmentAtPoint({
    segments,
    plotHeight,
    y,
}: SegmentAtPointParams): string | null {
    const height = plotHeight - y;
    let nearest: { seriesId: string; distance: number } | null = null;
    for (const { seriesId, y1, y2 } of segments) {
        const distance = Math.abs(height - (y1 + y2) / 2);
        const halfHit = (y2 - y1 + SEGMENT_GAP_PX) / 2;
        if (distance <= halfHit && (!nearest || distance < nearest.distance)) {
            nearest = { seriesId, distance };
        }
    }
    return nearest?.seriesId ?? null;
}
